import { useState, useEffect, useRef, useCallback } from 'react';
import { MapLibreMap, MapMarker, MapPolygon, useMapContext } from '@molecules/index';
import { Map } from 'maplibre-gl';
import { GeoJsonPolygon, validateGeoJsonPolygon, closePolygon } from '@/utils/geojson';
import { isPointInPolygon, calculatePolygonArea } from '@/utils/mapUtils';
import { geocodingService, GeocodingResult } from '@services/geocodingService';

type DrawingMode = 'polygon' | 'rectangle' | 'circle' | 'edit' | null;

interface ZoneDrawingMapProps {
  restaurantLat: number;
  restaurantLng: number;
  initialPolygon?: GeoJsonPolygon | null;
  onPolygonChange?: (polygon: GeoJsonPolygon | null) => void;
  onValidationChange?: (isValid: boolean, error?: string) => void;
  requireRestaurantInside?: boolean;
  className?: string;
}

export const ZoneDrawingMap = ({
  restaurantLat,
  restaurantLng,
  initialPolygon,
  onPolygonChange,
  onValidationChange,
  requireRestaurantInside = true,
  className = '',
}: ZoneDrawingMapProps) => {
  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(null);
  const [coordinates, setCoordinates] = useState<number[][]>([]);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [polygonArea, setPolygonArea] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(null);
  const [history, setHistory] = useState<number[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const rectangleStartRef = useRef<[number, number] | null>(null);
  const circleCenterRef = useRef<[number, number] | null>(null);
  const circleRadiusRef = useRef<number | null>(null);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchLocations(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const addToHistory = useCallback((coords: number[][]) => {
    setHistory((prevHistory) => {
      const newHistory = prevHistory.slice(0, historyIndex + 1);
      newHistory.push([...coords]);
      const newIndex = newHistory.length - 1;
      setHistoryIndex(newIndex);
      return newHistory;
    });
  }, [historyIndex]);

  const checkRestaurantInsidePolygon = useCallback((polygon: GeoJsonPolygon): boolean => {
    if (!restaurantLat || !restaurantLng) {
      return true;
    }
    const coords = polygon.coordinates[0];
    if (!coords || coords.length < 3) {
      return false;
    }
    
    const lat = Number(restaurantLat);
    const lng = Number(restaurantLng);
    const contains = isPointInPolygon(lng, lat, coords);
    
    return contains;
  }, [restaurantLat, restaurantLng]);

  const updatePolygon = useCallback((coords: number[][]) => {
    if (coords.length < 3) {
      setIsValid(false);
      setPolygonArea(null);
      if (onPolygonChange) {
        onPolygonChange(null);
      }
      if (onValidationChange) {
        onValidationChange(false, 'En az 3 nokta gerekli');
      }
      return;
    }

    const closed = closePolygon(coords);
    const polygon: GeoJsonPolygon = {
      type: 'Polygon',
      coordinates: [closed],
    };

    const validation = validateGeoJsonPolygon(polygon);
    let isValid = validation.isValid;
    let error = validation.error;

    if (requireRestaurantInside && isValid && restaurantLat && restaurantLng) {
      const containsRestaurant = checkRestaurantInsidePolygon(polygon);
      if (!containsRestaurant) {
        isValid = false;
        error = 'Polygon restoran konumunu (yeşil marker) içermelidir. Lütfen polygon\'u genişletin.';
      }
    }

    const area = calculatePolygonArea(closed);
    setPolygonArea(area);
    setIsValid(isValid);
    setValidationError(error);

    if (onPolygonChange) {
      onPolygonChange(isValid ? polygon : null);
    }

    if (onValidationChange) {
      onValidationChange(isValid, error);
      console.log('[ZoneDrawingMap] Validation changed:', { isValid, error, coordsLength: coords.length });
    }
  }, [restaurantLat, restaurantLng, onPolygonChange, onValidationChange, checkRestaurantInsidePolygon]);

  const initialPolygonLoadedRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    if (initialPolygon && initialPolygon.coordinates && initialPolygon.coordinates[0]) {
      const polygonKey = JSON.stringify(initialPolygon);
      
      if (initialPolygonLoadedRef.current !== polygonKey && !isInitializingRef.current) {
        console.log('[ZoneDrawingMap] Loading initial polygon:', polygonKey.substring(0, 100));
        isInitializingRef.current = true;
        const coords = initialPolygon.coordinates[0];
        setCoordinates(coords);
        addToHistory(coords);
        initialPolygonLoadedRef.current = polygonKey;
        
        const closed = closePolygon(coords);
        const polygon: GeoJsonPolygon = {
          type: 'Polygon',
          coordinates: [closed],
        };

        const validation = validateGeoJsonPolygon(polygon);
        let isValid = validation.isValid;
        let error = validation.error;

        if (requireRestaurantInside && isValid && restaurantLat && restaurantLng) {
          const containsRestaurant = checkRestaurantInsidePolygon(polygon);
          if (!containsRestaurant) {
            isValid = false;
            error = 'Polygon restoran konumunu (yeşil marker) içermelidir. Lütfen polygon\'u genişletin.';
          }
        }

        const area = calculatePolygonArea(closed);
        setPolygonArea(area);
        setIsValid(isValid);
        setValidationError(error);
        
        if (onValidationChange) {
          onValidationChange(isValid, error);
        }
        
        isInitializingRef.current = false;
      } else {
        console.log('[ZoneDrawingMap] Skipping initial polygon load - already loaded or initializing');
      }
    } else if (!initialPolygon) {
      console.log('[ZoneDrawingMap] Clearing initial polygon ref');
      initialPolygonLoadedRef.current = null;
      isInitializingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPolygon]);

  const handleMapLoad = useCallback((map: Map) => {
    setMapInstance(map);
    
    if (restaurantLat && restaurantLng) {
      map.flyTo({
        center: [Number(restaurantLng), Number(restaurantLat)],
        zoom: 14,
        duration: 0,
      });
    }
  }, [restaurantLat, restaurantLng]);

  const searchLocations = async (query: string) => {
    setIsSearching(true);
    try {
      const results = await geocodingService.searchAddresses(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Location search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationSelect = (location: GeocodingResult) => {
    setSelectedLocation({
      lat: location.latitude,
      lng: location.longitude,
      name: location.displayName,
    });

    if (mapInstance) {
      mapInstance.flyTo({
        center: [location.longitude, location.latitude],
        zoom: 15,
        duration: 1000,
      });
    }

    setSearchQuery('');
    setSearchResults([]);
  };

  const handleMapClick = useCallback((lng: number, lat: number) => {
    if (!drawingMode || !mapInstance) return;

    if (drawingMode === 'polygon') {
      const newCoordinates = [...coordinates, [lng, lat]];
      setCoordinates(newCoordinates);
      addToHistory(newCoordinates);
      updatePolygon(newCoordinates);
    } else if (drawingMode === 'rectangle') {
      if (!rectangleStartRef.current) {
        rectangleStartRef.current = [lng, lat];
        setCoordinates([[lng, lat]]);
      } else {
        const [startLng, startLat] = rectangleStartRef.current;
        const rectCoords = [
          [startLng, startLat],
          [lng, startLat],
          [lng, lat],
          [startLng, lat],
          [startLng, startLat],
        ];
        setCoordinates(rectCoords);
        addToHistory(rectCoords);
        updatePolygon(rectCoords);
        rectangleStartRef.current = null;
        setDrawingMode(null);
      }
    } else if (drawingMode === 'circle') {
      if (!circleCenterRef.current) {
        circleCenterRef.current = [lng, lat];
        setCoordinates([[lng, lat]]);
      } else {
        const [centerLng, centerLat] = circleCenterRef.current;
        const radius = Math.sqrt(
          Math.pow(lng - centerLng, 2) + Math.pow(lat - centerLat, 2)
        );
        circleRadiusRef.current = radius;
        
        const circleCoords: number[][] = [];
        const segments = 32;
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * 2 * Math.PI;
          const x = centerLng + radius * Math.cos(angle);
          const y = centerLat + radius * Math.sin(angle);
          circleCoords.push([x, y]);
        }
        setCoordinates(circleCoords);
        addToHistory(circleCoords);
        updatePolygon(circleCoords);
        circleCenterRef.current = null;
        circleRadiusRef.current = null;
        setDrawingMode(null);
      }
    }
  }, [drawingMode, coordinates, mapInstance, updatePolygon]);

  useEffect(() => {
    if (!mapInstance || drawingMode !== 'edit') return;

    const handleMouseDown = (e: any) => {
      const clickPoint = [e.lngLat.lng, e.lngLat.lat];
      let minDistance = Infinity;
      let closestIndex = -1;

      coordinates.forEach((coord, index) => {
        const pixel1 = mapInstance.project(coord);
        const pixel2 = mapInstance.project(clickPoint);
        const pixelDistance = Math.sqrt(
          Math.pow(pixel1.x - pixel2.x, 2) + Math.pow(pixel1.y - pixel2.y, 2)
        );
        if (pixelDistance < minDistance && pixelDistance < 20) {
          minDistance = pixelDistance;
          closestIndex = index;
        }
      });

      if (closestIndex !== -1) {
        setIsDragging(true);
        setDraggedPointIndex(closestIndex);
        e.preventDefault();
      }
    };

    const handleMouseMove = (e: any) => {
      if (!isDragging || draggedPointIndex === null) return;

      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;
      const newCoordinates = [...coordinates];
      newCoordinates[draggedPointIndex] = [lng, lat];
      setCoordinates(newCoordinates);
    };

    const handleMouseUp = () => {
      if (isDragging && draggedPointIndex !== null) {
        const finalCoordinates = [...coordinates];
        setIsDragging(false);
        setDraggedPointIndex(null);
        addToHistory(finalCoordinates);
        updatePolygon(finalCoordinates);
      }
    };

    mapInstance.on('mousedown', handleMouseDown);
    mapInstance.on('mousemove', handleMouseMove);
    mapInstance.on('mouseup', handleMouseUp);

    return () => {
      mapInstance.off('mousedown', handleMouseDown);
      mapInstance.off('mousemove', handleMouseMove);
      mapInstance.off('mouseup', handleMouseUp);
    };
  }, [mapInstance, drawingMode, coordinates, isDragging, draggedPointIndex, updatePolygon]);

  const startDrawing = (mode: 'polygon' | 'rectangle' | 'circle') => {
    setDrawingMode(mode);
    setCoordinates([]);
    setIsValid(false);
    setValidationError(undefined);
    rectangleStartRef.current = null;
    circleCenterRef.current = null;
    circleRadiusRef.current = null;
  };

  const startEdit = () => {
    if (coordinates.length < 3) return;
    setDrawingMode('edit');
  };

  const stopDrawing = () => {
    setDrawingMode(null);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const prevCoords = history[newIndex];
      setCoordinates(prevCoords);
      updatePolygon(prevCoords);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const nextCoords = history[newIndex];
      setCoordinates(nextCoords);
      updatePolygon(nextCoords);
    }
  };

  const deletePoint = (index: number) => {
    if (coordinates.length <= 3) return;
    const newCoordinates = coordinates.filter((_, i) => i !== index);
    setCoordinates(newCoordinates);
    addToHistory(newCoordinates);
    updatePolygon(newCoordinates);
  };

  const clearPolygon = () => {
    setCoordinates([]);
    setIsValid(false);
    setValidationError(undefined);
    setDrawingMode(null);
    setPolygonArea(null);
    setHistory([]);
    setHistoryIndex(-1);
    if (onPolygonChange) {
      onPolygonChange(null);
    }
    if (onValidationChange) {
      onValidationChange(false);
    }
  };

  const currentPolygon: GeoJsonPolygon | null = coordinates.length >= 3
    ? {
        type: 'Polygon',
        coordinates: [closePolygon(coordinates)],
      }
    : null;

  const formatArea = (area: number): string => {
    if (area < 0.01) {
      return `${(area * 1000000).toFixed(0)} m²`;
    } else if (area < 1) {
      return `${(area * 100).toFixed(2)} hektar`;
    } else {
      return `${area.toFixed(2)} km²`;
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Semt veya mahalle ara (örn: Seyhan, Çukurova)"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {searchResults.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((result, index) => (
              <button
                key={index}
                onClick={() => handleLocationSelect(result)}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
              >
                <p className="font-medium text-gray-900 text-sm">{result.displayName}</p>
                {result.district && result.city && (
                  <p className="text-xs text-gray-600">
                    {result.district}, {result.city}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="h-[500px] rounded-lg overflow-hidden border border-gray-200 bg-gray-100 relative">
        <MapLibreMap
          center={[Number(restaurantLng), Number(restaurantLat)]}
          zoom={14}
          onMapLoad={handleMapLoad}
          onMapClick={handleMapClick}
          className="w-full h-full"
        >
          <MapMarker
            position={[Number(restaurantLng), Number(restaurantLat)]}
            color="#10b981"
            size={40}
            popup={`Restoran Konumu\n[${Number(restaurantLng).toFixed(6)}, ${Number(restaurantLat).toFixed(6)}]`}
          />
          {selectedLocation && (
            <MapMarker
              position={[selectedLocation.lng, selectedLocation.lat]}
              color="#3b82f6"
              size={28}
              popup={selectedLocation.name}
            />
          )}
          {coordinates.map((coord, index) => (
            <MapMarker
              key={index}
              position={coord}
              color={drawingMode === 'edit' ? '#E63946' : isDragging && draggedPointIndex === index ? '#f59e0b' : '#3b82f6'}
              size={drawingMode === 'edit' ? 32 : 24}
              popup={
                drawingMode === 'edit' ? (
                  <div className="text-center">
                    <p className="text-xs font-medium mb-1">Nokta {index + 1}</p>
                    <button
                      onClick={() => deletePoint(index)}
                      className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                    >
                      Sil
                    </button>
                  </div>
                ) : undefined
              }
            />
          ))}
          {currentPolygon && (
            <MapPolygon
              polygon={currentPolygon}
              fillColor={isValid ? '#10b981' : '#ef4444'}
              fillOpacity={0.2}
              strokeColor={isValid ? '#10b981' : '#ef4444'}
              strokeWidth={2}
            />
          )}
        </MapLibreMap>

        {drawingMode && (
          <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-gray-200">
            <p className="text-sm font-medium text-gray-700">
              {drawingMode === 'polygon' && 'Haritaya tıklayarak nokta ekleyin'}
              {drawingMode === 'rectangle' && rectangleStartRef.current ? 'İkinci köşeyi seçin' : 'Dikdörtgenin ilk köşesini seçin'}
              {drawingMode === 'circle' && circleCenterRef.current ? 'Dairenin yarıçapını belirleyin' : 'Dairenin merkezini seçin'}
              {drawingMode === 'edit' && 'Noktaları sürükleyerek düzenleyin'}
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {!drawingMode ? (
            <>
              <button
                onClick={() => startDrawing('polygon')}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Serbest Çizim
              </button>
              <button
                onClick={() => startDrawing('rectangle')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                </svg>
                Dikdörtgen
              </button>
              <button
                onClick={() => startDrawing('circle')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Daire
              </button>
              {coordinates.length >= 3 && (
                <button
                  onClick={startEdit}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Düzenle
                </button>
              )}
            </>
          ) : (
            <button
              onClick={stopDrawing}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Çizimi Bitir
            </button>
          )}

          {coordinates.length > 0 && (
            <>
              <button
                onClick={undo}
                disabled={historyIndex <= 0}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                title="Geri Al"
              >
                ↶ Geri Al
              </button>
              <button
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                title="İleri Al"
              >
                ↷ İleri Al
              </button>
              <button
                onClick={clearPolygon}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Temizle
              </button>
            </>
          )}
        </div>

        {coordinates.length > 0 && (
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">
              <strong>{coordinates.length}</strong> nokta
            </span>
            {polygonArea !== null && (
              <span className="text-gray-600">
                Alan: <strong>{formatArea(polygonArea)}</strong>
              </span>
            )}
          </div>
        )}
      </div>

      {validationError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{validationError}</p>
              {validationError?.includes('restoran') && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-red-600">
                    💡 İpucu: Haritadaki <strong>yeşil marker</strong> restoran konumunuzdur. Polygon'u bu marker'ı içerecek şekilde çizin.
                  </p>
                  <p className="text-xs text-red-600">
                    Restoran konumu: [{Number(restaurantLng).toFixed(6)}, {Number(restaurantLat).toFixed(6)}]
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isValid && currentPolygon && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="font-medium">✓ Polygon geçerli. Kaydedebilirsiniz.</p>
              {polygonArea !== null && (
                <p className="text-xs mt-1 text-green-600">Alan: {formatArea(polygonArea)}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
